import { useMutation } from '@apollo/client/react'
import { UntitledIcon } from '@faceup/icons'
import { ulMinusCircle } from '@faceup/icons/ulMinusCircle'
import { ulPlus } from '@faceup/icons/ulPlus'
import { useLanguageName } from '@faceup/localization'
import { useNavigate } from '@faceup/router'
import { Form } from '@faceup/ui'
import { Button, Input, ModalConfirm, Select, Switch } from '@faceup/ui-base'
import {
  Country,
  convertCountryToCountryName,
  type Institution,
  isCountrySanctioned,
  isEmail,
  type Language,
} from '@faceup/utils'
import moment from 'moment-timezone'
import { useRef, useState } from 'react'
import { graphql } from '../../__generated__'
import { useAdminAccessRights } from '../../Contexts/AdminAccessRightsProvider'

const mutations = {
  CreateDemoCompany: graphql(`
    mutation CreateDemoCompany($input: CreateDemoCompanyInput!) {
      createDemoCompany(input: $input) {
        company {
          id
        }
      }
    }
  `),
}

type CreateCompanyProps = {
  institutionType: Institution
}

export const CreateCompany = ({ institutionType }: CreateCompanyProps) => {
  const { isAllowedDemoCompany } = useAdminAccessRights()
  const [visible, setVisible] = useState(false)

  const [name, setName] = useState({ value: '', error: false })
  const [country, setCountry] = useState<{ value: Country | undefined; error: boolean }>({
    value: undefined,
    error: false,
  })
  const [language, setLanguage] = useState<{ value: Language | undefined; error: boolean }>({
    value: undefined,
    error: false,
  })
  const [timezone, setTimezone] = useState({ value: '', error: false })
  const [importMockData, setImportMockData] = useState(false)
  const [timezones, setTimezones] = useState(moment.tz.names())
  const memberIdRef = useRef(0)
  const [members, setMembers] = useState<
    {
      id: number
      error: boolean
      email: { value: string }
    }[]
  >([
    {
      id: memberIdRef.current++,
      error: false,
      email: { value: '' },
    },
  ])

  const navigate = useNavigate()
  const { getLanguageList } = useLanguageName()

  const resetForm = () => {
    setName({ value: '', error: false })
    setCountry({ value: undefined, error: false })
    setLanguage({ value: undefined, error: false })
    setTimezone({ value: '', error: false })
    setImportMockData(false)
    setTimezones(moment.tz.names())
    setMembers([])
  }

  const [createDemoCompany, { loading }] = useMutation(mutations.CreateDemoCompany, {
    onCompleted: data => {
      setVisible(!visible)
      resetForm()
      navigate(routes => routes.institution({ id: data.createDemoCompany?.company?.id ?? '' }))
    },
  })

  const validateForm = () => {
    let ok = true

    if (!name.value) {
      setName({ ...name, error: true })
      ok = false
    }
    if (!country.value) {
      setCountry({ ...country, error: true })
      ok = false
    }
    if (!language.value) {
      setLanguage({ ...language, error: true })
      ok = false
    }
    if (!timezone.value) {
      setTimezone({ ...timezone, error: true })
      ok = false
    }

    setMembers(
      members.map(member => {
        if (!member.email.value || !isEmail(member.email.value.trim())) {
          ok = false
          return { ...member, error: true }
        }

        return member
      })
    )

    return ok
  }

  return (
    <>
      {isAllowedDemoCompany && (
        <Button
          type='primary'
          onClick={() => setVisible(true)}
          icon={<UntitledIcon icon={ulPlus} />}
        >
          Register DEMO institution
        </Button>
      )}

      <ModalConfirm
        title='Register DEMO institution'
        open={visible}
        onConfirm={() => {
          if (!validateForm()) {
            return
          }
          createDemoCompany({
            variables: {
              input: {
                institutionType,
                name: name.value,
                country: country.value as Country,
                language: language.value as Language,
                membersEmails: members.map(m => m.email.value.trim()),
                timezone: timezone.value,
                importMockData,
              },
            },
          })
        }}
        onCancel={() => setVisible(!visible)}
        onClose={() => setVisible(!visible)}
        confirmState={{
          loading,
        }}
      >
        <Form>
          <Form.Item
            label='Organization name'
            errorMessage={name.error && 'Enter organization name'}
          >
            <Input
              placeholder='Apple Inc.'
              value={name.value}
              onChange={({ target: { value } }) => {
                setName({ error: false, value })
              }}
            />
          </Form.Item>
          <Form.Item label='Country' errorMessage={country.error && 'Select country'}>
            <Select
              placeholder='Select country'
              onChange={value => {
                setCountry({ error: false, value })
              }}
              value={country.value}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={Object.values(Country)
                .filter(country => !isCountrySanctioned(country))
                .map(country => ({
                  label: convertCountryToCountryName(country),
                  value: country,
                }))}
            />
          </Form.Item>
          <Form.Item label='Language' errorMessage={language.error && 'Select language'}>
            <Select
              placeholder='Select language'
              onChange={value => {
                setLanguage({ error: false, value })
              }}
              value={language.value}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={getLanguageList().map(language => ({
                label: language.translatedName,
                value: language.language,
              }))}
            />
          </Form.Item>
          <Form.Item label='Timezone' errorMessage={timezone.error && 'Select timezone'}>
            <Select<string>
              placeholder='Select timezone'
              onChange={value => {
                setTimezone({ error: false, value })
              }}
              value={timezone.value}
              showSearch
              onSearch={v => {
                const t = moment.tz
                  .names()
                  .filter(element => element.toLowerCase().includes(v.toLowerCase()))
                setTimezones(t)
              }}
              options={timezones.map(t => ({ label: t, value: t }))}
            />
          </Form.Item>

          <Form.Item label='Import Demo Data'>
            <Switch onChange={checked => setImportMockData(checked)} checked={importMockData} />
          </Form.Item>

          {members.map((field, index) => (
            <Form.Item
              key={field.id}
              label={
                <>
                  {index === 0 ? 'Account Owner' : `Administrator ${index + 1}`}
                  {Boolean(index) && ( // main member
                    <UntitledIcon
                      icon={ulMinusCircle}
                      style={{ marginLeft: 10 }}
                      onClick={() => {
                        setMembers(members.filter((_, i) => i !== index))
                      }}
                    />
                  )}
                </>
              }
              errorMessage={field.error && 'Enter valid email'}
            >
              <Input
                style={{ marginTop: 10 }}
                placeholder='Email'
                value={field.email.value}
                onChange={({ target: { value } }) => {
                  setMembers(
                    members.map((member, i) =>
                      i === index ? { ...member, email: { value }, error: false } : member
                    )
                  )
                }}
              />
            </Form.Item>
          ))}
          <Form.Item>
            <Button
              ghost
              type='primary'
              onClick={() => {
                setMembers(prevState => [
                  ...prevState,
                  {
                    id: memberIdRef.current++,
                    error: false,
                    email: { value: '' },
                  },
                ])
              }}
            >
              <UntitledIcon icon={ulPlus} /> Add Administrator
            </Button>
          </Form.Item>
        </Form>
      </ModalConfirm>
    </>
  )
}
